import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import { rangeFor, toQuery, useAnalyticsFilters } from './filters';
import { fmtMin, fmtNum, fmtPct } from './format';
import { alert, card, cardHead, muted, td, tdR, th, thR } from './Overview';

// "Standardy / targety" (spec 7.11): editable line × material table. The target is the
// denominator of Performance; best demonstrated (p95 of long production runs) is the hint.

interface Row {
  line_id: number; line_name: string; material_id: number; material_name: string; has_density: boolean;
  prodMin: number; avgSpeed: number | null; bestDemonstrated: number | null; target: number | null;
  targetIsDefault: boolean; minSpeed: number | null; validFrom: string | null; targetKgPerH: number | null;
  performance: number | null; standard_id: number | null;
}
interface HistoryRow { id: number; line_id: number | null; line_name: string | null; material_id: number; material_name: string; target: number; minSpeed: number | null; validFrom: string; notes: string | null }
interface Data { rows: Row[]; best_days: number; best_min_run_minutes: number; history: HistoryRow[] }

const toLocalDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Standards: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  // New targets default to the start of the analysed period so Performance shows up for it right away.
  const [validFrom, setValidFrom] = useState(() => toLocalDate(rangeFor(filters).from));
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const { from, to, line_id, material_id } = toQuery(filters);
    api.get('/analytics/standards', { params: { from, to, line_id, material_id } })
      .then(res => { setData(res.data); setError(null); setEdits({}); })
      .catch(err => setError(err?.response?.data?.message ?? t('analytics.loadError')))
      .finally(() => setLoading(false));
  }, [filters, t]);

  useEffect(() => { load(); }, [load]);

  const key = (r: Row) => `${r.line_id}|${r.material_id}`;
  const dirty = useMemo(() => Object.entries(edits).filter(([, v]) => v.trim() !== ''), [edits]);

  const saveAll = async (rows: { line_id: number; material_id: number; target: number }[]) => {
    if (!rows.length) return;
    setSaving(true);
    try {
      await api.post('/analytics/standards/bulk', {
        standards: rows.map(r => ({ production_line_id: r.line_id, material_id: r.material_id, target_speed_m_min: r.target, valid_from: validFrom })),
      });
      load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? t('analytics.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const saveEdits = () => saveAll(dirty.map(([k, v]) => {
    const [line_id, material_id] = k.split('|').map(Number);
    return { line_id, material_id, target: parseFloat(v.replace(',', '.')) };
  }).filter(r => !Number.isNaN(r.target) && r.target > 0));

  const setAllBest = () => saveAll((data?.rows ?? []).filter(r => r.bestDemonstrated !== null).map(r => ({ line_id: r.line_id, material_id: r.material_id, target: r.bestDemonstrated as number })));

  const removeStandard = async (h: HistoryRow) => {
    if (!window.confirm(t('standards.confirmDelete'))) return;
    try { await api.delete(`/analytics/standards/${h.id}`); load(); } catch (err: any) { setError(err?.response?.data?.message ?? t('analytics.saveError')); }
  };

  const perfColor = (p: number | null) => (p === null ? undefined : p >= 0.9 ? '#059669' : p >= 0.75 ? '#d97706' : '#dc2626');

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>🎯 {t('standards.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14, maxWidth: 900 }}>{t('standards.subtitle')}</p>
      </div>

      <AnalyticsFilterBar filters={filters} onChange={setFilters} showCompare={false} />
      {error && <div style={alert}>{error}</div>}

      <div style={card}>
        <div style={{ ...cardHead, alignItems: 'center' }}>
          <div>
            <b>{t('standards.tableTitle')}</b>
            <div style={muted}>{t('standards.tableSub', { days: String(data?.best_days ?? 90), min: String(data?.best_min_run_minutes ?? 20) })}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
              {t('standards.validFrom')}
              <input type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} style={inp} />
            </label>
            <button onClick={setAllBest} disabled={saving || !data?.rows.some(r => r.bestDemonstrated !== null)} style={btnSecondary}>{t('standards.setAllBest')}</button>
            <button onClick={saveEdits} disabled={saving || dirty.length === 0} style={{ ...btnPrimary, opacity: dirty.length ? 1 : 0.5 }}>
              {saving ? '…' : t('standards.saveChanges', { count: String(dirty.length) })}
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={th}>{t('analytics.colLine')}</th>
                <th style={th}>{t('standards.colMaterial')}</th>
                <th style={thR}>{t('standards.colProdHours')}</th>
                <th style={thR}>{t('standards.colAvgSpeed')}</th>
                <th style={thR}>{t('standards.colBest')}</th>
                <th style={thR}>{t('standards.colTarget')}</th>
                <th style={thR}>{t('standards.colTargetKgH')}</th>
                <th style={thR}>{t('standards.colValidFrom')}</th>
                <th style={thR}>Performance</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('common.loading')}</td></tr>
              ) : !data?.rows.length ? (
                <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('standards.empty')}</td></tr>
              ) : data.rows.map(r => {
                const k = key(r);
                const editing = edits[k] ?? '';
                return (
                  <tr key={k} style={{ background: editing ? '#fffbeb' : undefined }}>
                    <td style={{ ...td, fontWeight: 700 }}>{r.line_name}</td>
                    <td style={td}>
                      {r.material_name}
                      {!r.has_density && <span style={{ ...muted, marginLeft: 6, color: '#d97706' }}>{t('standards.noDensity')}</span>}
                    </td>
                    <td style={tdR}>{fmtMin(r.prodMin)}</td>
                    <td style={tdR}>{fmtNum(r.avgSpeed, 1)}</td>
                    <td style={{ ...tdR, color: '#0f9d58', fontWeight: 600 }}>{fmtNum(r.bestDemonstrated, 1)}</td>
                    <td style={tdR}>
                      <input
                        value={editing || (r.target !== null ? String(r.target) : '')}
                        onChange={e => setEdits({ ...edits, [k]: e.target.value })}
                        placeholder="—"
                        style={{ ...inp, width: 80, textAlign: 'right', fontWeight: 700, borderColor: editing ? '#f59e0b' : '#cbd5e1' }}
                      />
                      {r.targetIsDefault && !editing && <div style={{ ...muted, fontSize: 10 }}>{t('standards.defaultTarget')}</div>}
                    </td>
                    <td style={tdR}>{fmtNum(r.targetKgPerH, 1)}</td>
                    <td style={{ ...tdR, color: '#64748b' }}>{r.validFrom ?? '—'}</td>
                    <td style={{ ...tdR, fontWeight: 700, color: perfColor(r.performance) }}>{fmtPct(r.performance)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {r.bestDemonstrated !== null && (
                        <button onClick={() => setEdits({ ...edits, [k]: String(r.bestDemonstrated) })} style={btnSmall}>{t('standards.useBest')}</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={card}>
        <div style={{ ...cardHead, alignItems: 'center' }}>
          <b>{t('standards.historyTitle')} ({data?.history.length ?? 0})</b>
          <button onClick={() => setShowHistory(v => !v)} style={btnSmall}>{showHistory ? t('materials.hide') : t('materials.show')}</button>
        </div>
        {showHistory && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={th}>{t('analytics.colLine')}</th><th style={th}>{t('standards.colMaterial')}</th><th style={thR}>{t('standards.colTarget')}</th><th style={thR}>{t('standards.colMinSpeed')}</th><th style={thR}>{t('standards.colValidFrom')}</th><th style={th}>{t('materials.colNotes')}</th><th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {data?.history.map(h => (
                <tr key={h.id}>
                  <td style={td}>{h.line_name ?? <i style={muted}>{t('standards.allLines')}</i>}</td>
                  <td style={td}>{h.material_name}</td>
                  <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(h.target, 1)}</td>
                  <td style={tdR}>{fmtNum(h.minSpeed, 1)}</td>
                  <td style={tdR}>{h.validFrom}</td>
                  <td style={{ ...td, color: '#64748b' }}>{h.notes ?? ''}</td>
                  <td style={td}><button onClick={() => removeStandard(h)} style={{ ...btnSmall, color: '#dc3545' }}>🗑</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const inp: React.CSSProperties = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 };
const btnSmall: React.CSSProperties = { padding: '5px 10px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#475569', cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { ...btnSmall, padding: '8px 14px', fontSize: 13 };
const btnPrimary: React.CSSProperties = { padding: '8px 16px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' };

export default Standards;
